import { useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserHoverCard } from '@/components/ui/user-hover-card';
import { SkeletonStreamCard } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Play, Clock, Calendar, Info, MoreVertical, Pencil, Settings, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePermissions } from '@/hooks/use-permissions';
import { StreamDetailsSheet } from './stream-details-sheet';
import { RealtimeViewerCount } from './realtime-viewer-count';
import { ContentAccessIndicator } from '@/components/ui/content-access-indicator';
import { getContentAccessLevelFromTags } from '@/lib/utils/content-access';
import { MediaImage } from '@/components/ui/media-image';
import type { Stream } from '@/lib/stores/streams/types';

interface StreamCardProps {
  stream: Stream;
  loading?: boolean;
  onDetailsClick?: (stream: Stream) => void;
}

export function StreamCard({ stream, loading = false, onDetailsClick }: StreamCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile({ containerRef, breakpoint: 620 });
  const { user, canManageStreams, can } = usePermissions();
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);

  if (!stream || !stream.id || isNaN(stream.id) || stream.id <= 0) {
    return null;
  }

  const isCreator = user?.id === stream.createdBy;
  const canEdit = canManageStreams || isCreator;
  const canDelete = can('delete_streams');

  if (loading) {
    return <SkeletonStreamCard />;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="animate-pulse">LIVE</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline">Ended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return <Play className="h-4 w-4 text-destructive" />;
      case 'scheduled':
        return <Clock className="h-4 w-4" />;
      case 'ended':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Play className="h-4 w-4" />;
    }
  };

  const formatScheduledTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} away`;
    }

    if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }

    return 'Starting soon';
  };

  return (
    <div ref={containerRef} className="@container/stream-card">
      <Card className="group overflow-hidden border-0 shadow-sm transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10">
        <div className="relative aspect-video overflow-hidden bg-linear-to-br from-muted to-muted/50">
          {stream.thumbnailUrl ? (
            <MediaImage src={stream.thumbnailUrl} alt={stream.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <div className="text-center">
                {getStatusIcon(stream.status)}
                <p className="mt-2 text-sm text-muted-foreground">
                  {stream.status === 'live' ? 'Live Stream' : 'Scheduled'}
                </p>
              </div>
            </div>
          )}

          <div className="absolute left-3 top-3 z-10">{getStatusBadge(stream.status)}</div>

          {canEdit && (
            <div className="absolute right-3 top-3 z-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 border-0 bg-card/70 text-card-foreground hover:bg-card/90"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={`/admin/streams/${stream.id}`}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit Stream
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={`/admin/streams/${stream.id}?tab=access`}>
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Access
                    </Link>
                  </DropdownMenuItem>
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive focus:text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Stream
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {stream.status === 'live' && (
            <div className="absolute right-3 top-3 rounded bg-card/70 px-2 py-1 text-xs text-card-foreground backdrop-blur-sm">
              <RealtimeViewerCount
                streamId={stream.id}
                fallbackCount={stream.viewerCount || 0}
                className="text-white"
              />
            </div>
          )}

          <div className="absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </div>

        <CardContent className="p-3 @[520px]/stream-card:p-4">
          <Link to={`/streams/${stream.id}`}>
            <h3 className="mb-2 line-clamp-2 cursor-pointer text-lg font-semibold transition-colors duration-200 group-hover:text-primary hover:underline">
              {stream.title}
            </h3>
          </Link>

          <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">{stream.description}</p>

          <div className="mb-3 flex items-center gap-2">
            <UserHoverCard
              user={{
                name: stream.creator?.name || `Creator #${stream.createdBy}`,
                profilePictureUrl: stream.creator?.profilePictureUrl,
              }}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={stream.creator?.profilePictureUrl} />
                <AvatarFallback className="text-xs">
                  {stream.creator?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </UserHoverCard>
            <span className="text-sm text-muted-foreground">
              {stream.creator?.name || `Creator #${stream.createdBy}`}
            </span>
          </div>

          {stream.status === 'scheduled' && stream.scheduledStart && (
            <div className="mb-3 flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {stream.sourceType === 'upload'
                ? `Video release: ${formatScheduledTime(stream.scheduledStart)}`
                : formatScheduledTime(stream.scheduledStart)}
            </div>
          )}

          {(() => {
            const tags = Array.isArray(stream.tags) ? stream.tags : [];
            return tags.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {tags.slice(0, 3).map((tag: string) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {tags.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{tags.length - 3}
                  </Badge>
                )}
              </div>
            );
          })()}

          {(() => {
            const tags = Array.isArray(stream.tags) ? stream.tags : [];
            return tags.length > 0 && (
              <div className="mb-4">
                <ContentAccessIndicator
                  accessLevel={getContentAccessLevelFromTags(tags)}
                  className="w-fit"
                />
              </div>
            );
          })()}

          <div className={isMobile ? 'grid grid-cols-1 gap-2 @[460px]/stream-card:grid-cols-2' : 'flex gap-2'}>
            {isMobile ? (
              <>
                <Button
                  variant="outline"
                  className="w-full transition-all duration-200 group-hover:shadow-md"
                  onClick={() => {
                    if (onDetailsClick) {
                      onDetailsClick(stream);
                      return;
                    }
                    setShowDetailsSheet(true);
                  }}
                >
                  <Info className="mr-2 h-4 w-4" />
                  View Details
                </Button>
                <Button asChild className="w-full transition-all duration-200 group-hover:shadow-md">
                  <Link to={`/streams/${stream.id}`}>
                    {stream.status === 'live' ? (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Watch
                      </>
                    ) : stream.status === 'scheduled' ? (
                      <>
                        <Clock className="mr-2 h-4 w-4" />
                        View
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        View
                      </>
                    )}
                  </Link>
                </Button>
              </>
            ) : (
              <Button asChild className="w-full transition-all duration-200 group-hover:shadow-md">
                <Link to={`/streams/${stream.id}`}>
                  {stream.status === 'live' ? (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Watch Live
                    </>
                  ) : stream.status === 'scheduled' ? (
                    <>
                      <Clock className="mr-2 h-4 w-4" />
                      View Details
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      View Recording
                    </>
                  )}
                </Link>
              </Button>
            )}
          </div>
        </CardContent>

        <StreamDetailsSheet
          stream={stream}
          open={showDetailsSheet}
          onOpenChange={setShowDetailsSheet}
        />
      </Card>
    </div>
  );
}
